import React, { useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import ScrollToTop from "../ScrollToTop";
import AdminSidebar from "../../Component/AdminSidebar";
import { loadCatalog, LOW_STOCK_THRESHOLD } from "../../data/catalog";
import {
  formatOrderAmount,
  formatOrderDate,
  getOrderLifecycleBadgeClass,
  getOrderLifecycleLabel,
  loadOrders,
} from "../../data/orders";
import {
  revenueByDay,
  revenueByWeek,
  revenueByMonth,
  busiestHours,
  topSellingProducts,
  paymentMethodBreakdown,
  computeDashboardMetrics,
} from "../../data/analytics";
import { computeMargin } from "../../data/taxRates";
import { loadInventoryLots } from "../../data/inventory";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

const chartDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "#1b1b18",
      titleColor: "#fffdf9",
      bodyColor: "#fffdf9",
      cornerRadius: 8,
      padding: 10,
    },
  },
};

const lineChartOpts = {
  ...chartDefaults,
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
    y: {
      grid: { color: "rgba(0,0,0,0.05)" },
      ticks: { font: { size: 11 }, callback: (v) => `${v} EUR` },
    },
  },
  elements: { point: { radius: 2, hoverRadius: 5 } },
};

const barChartOpts = {
  ...chartDefaults,
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
    y: {
      grid: { color: "rgba(0,0,0,0.05)" },
      ticks: { font: { size: 11 }, callback: (v) => `${v} EUR` },
    },
  },
};

const doughnutOpts = {
  ...chartDefaults,
  cutout: "65%",
  plugins: {
    ...chartDefaults.plugins,
    legend: { display: true, position: "bottom", labels: { font: { size: 11 }, padding: 12 } },
  },
};

const revenueTrendBadge = (trend) => {
  if (trend > 0) return <span className="badge bg-success ms-2">+{trend.toFixed(1)}%</span>;
  if (trend < 0) return <span className="badge bg-danger ms-2">{trend.toFixed(1)}%</span>;
  return <span className="badge bg-secondary ms-2">0%</span>;
};

const MyAccountOrder = () => {
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [revenueRange, setRevenueRange] = useState("30j");
  const [topCount, setTopCount] = useState(5);
  const [lots, setLots] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      const [loadedProducts, loadedOrders] = await Promise.all([
        loadCatalog(),
        loadOrders(),
      ]);
      const loadedLots = await loadInventoryLots();
      if (!isMounted) return;
      setProducts(loadedProducts);
      setOrders(loadedOrders);
      setLots(loadedLots);
      setLoaderStatus(false);
    };
    fetchData();
    return () => { isMounted = false; };
  }, []);

  const metrics = useMemo(() => computeDashboardMetrics(orders, products), [orders, products]);

  const revenueData = useMemo(() => {
    switch (revenueRange) {
      case "7j": return revenueByDay(orders, 7);
      case "30j": return revenueByDay(orders, 30);
      case "12s": return revenueByWeek(orders, 12);
      case "12m": return revenueByMonth(orders, 12);
      default: return revenueByDay(orders, 30);
    }
  }, [orders, revenueRange]);

  const revenueChartData = useMemo(() => {
    const labels = revenueData.map((d) => d.date || d.label || d.weekStart || d.month);
    return {
      labels,
      datasets: [
        {
          label: "Chiffre d'affaires",
          data: revenueData.map((d) => Number(d.revenue.toFixed(2))),
          borderColor: "#8b3d12",
          backgroundColor: "rgba(139,61,18,0.08)",
          fill: true,
          tension: 0.35,
        },
      ],
    };
  }, [revenueData]);

  const hoursData = useMemo(() => busiestHours(orders, 30), [orders]);

  const hoursChartData = useMemo(() => ({
    labels: hoursData.map((h) => `${String(h.hour).padStart(2, "0")}h`),
    datasets: [
      {
        label: "Commandes",
        data: hoursData.map((h) => h.orders),
        backgroundColor: "rgba(139,61,18,0.7)",
        borderRadius: 4,
      },
    ],
  }), [hoursData]);

  const topProducts = useMemo(() => topSellingProducts(orders, topCount), [orders, topCount]);

  const paymentData = useMemo(() => paymentMethodBreakdown(orders), [orders]);

  const paymentChartData = useMemo(() => {
    const colors = ["#8b3d12", "#d4a24e", "#2d6a4f", "#e76f51", "#457b9d", "#6c757d"];
    return {
      labels: paymentData.map((p) => p.method),
      datasets: [
        {
          data: paymentData.map((p) => Number(p.revenue.toFixed(2))),
          backgroundColor: colors.slice(0, paymentData.length),
          borderWidth: 2,
          borderColor: "#fffdf9",
        },
      ],
    };
  }, [paymentData]);

  const recentOrders = useMemo(() => orders.slice(0, 8), [orders]);

  const lowStockProducts = useMemo(
    () =>
      products
        .filter((p) => p.stock <= LOW_STOCK_THRESHOLD)
        .sort((a, b) => a.stock - b.stock)
        .slice(0, 6),
    [products]
  );

  const avgMargin = useMemo(() => {
    const productMap = new Map(products.map((p) => [p.id, p]));
    const lotsWithPurchase = lots.filter((l) => l.purchasePriceHt > 0);
    if (lotsWithPurchase.length === 0) return null;
    const margins = lotsWithPurchase
      .map((l) => {
        const product = productMap.get(l.productId);
        const sellingHT = product?.price || 0;
        if (sellingHT <= 0) return null;
        return computeMargin(sellingHT, l.purchasePriceHt);
      })
      .filter((m) => m !== null);
    if (margins.length === 0) return null;
    return (margins.reduce((s, m) => s + m, 0) / margins.length).toFixed(1);
  }, [lots, products]);

  return (
    <div>
      <ScrollToTop />
      <section className="admin-section">
        <div className="container">
          <div className="row">
            <AdminSidebar activePath="/MyAccountOrder" />

            <div className="col-lg-9 col-md-8 col-12">
              {loaderStatus ? (
                <div className="p-6 p-lg-10">
                  <div className="alert alert-light border mb-0">
                    Chargement du tableau de bord...
                  </div>
                </div>
              ) : (
                <div className="p-6 p-lg-10">
                  {/* Header */}
                  <div className="mb-6">
                    <h2 className="mb-1">Tableau de bord</h2>
                    <p className="text-muted mb-0">
                      Vue d'ensemble des ventes, commandes et stock Almoune.
                    </p>
                  </div>

                  {/* KPI Cards - Row 1 */}
                  <div className="row g-3 mb-6">
                    <div className="col-md-3 col-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-3">
                          <div className="d-flex align-items-center mb-2">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                              style={{ width: 36, height: 36, background: "rgba(139,61,18,0.1)" }}>
                              <i className="fas fa-euro-sign text-primary" style={{ fontSize: 14 }} />
                            </div>
                            <small className="text-muted">CA aujourd'hui</small>
                          </div>
                          <h4 className="mb-0" style={{ fontWeight: 700 }}>
                            {formatOrderAmount(metrics.todayRevenue)}
                          </h4>
                          <small className="text-muted">
                            hier: {formatOrderAmount(metrics.yesterdayRevenue)}
                            {revenueTrendBadge(metrics.revenueTrend)}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-3">
                          <div className="d-flex align-items-center mb-2">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                              style={{ width: 36, height: 36, background: "rgba(45,106,79,0.1)" }}>
                              <i className="fas fa-shopping-bag" style={{ fontSize: 14, color: "#2d6a4f" }} />
                            </div>
                            <small className="text-muted">Commandes aujourd'hui</small>
                          </div>
                          <h4 className="mb-0" style={{ fontWeight: 700 }}>{metrics.todayOrders}</h4>
                          <small className="text-muted">
                            total: {metrics.totalOrders} · actives: {metrics.activeOrders}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-3">
                          <div className="d-flex align-items-center mb-2">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                              style={{ width: 36, height: 36, background: "rgba(212,162,78,0.15)" }}>
                              <i className="fas fa-chart-line" style={{ fontSize: 14, color: "#b8860b" }} />
                            </div>
                            <small className="text-muted">CA total</small>
                          </div>
                          <h4 className="mb-0" style={{ fontWeight: 700 }}>
                            {formatOrderAmount(metrics.totalRevenue)}
                          </h4>
                          <small className="text-muted">
                            panier moyen: {formatOrderAmount(metrics.avgOrderValue)}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-3">
                          <div className="d-flex align-items-center mb-2">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                              style={{ width: 36, height: 36, background: "rgba(231,111,81,0.1)" }}>
                              <i className="fas fa-boxes-stacked" style={{ fontSize: 14, color: "#e76f51" }} />
                            </div>
                            <small className="text-muted">Produits</small>
                          </div>
                          <h4 className="mb-0" style={{ fontWeight: 700 }}>{metrics.totalProducts}</h4>
                          <small className="text-muted">
                            {metrics.lowStockCount > 0 ? (
                              <span className="text-danger">{metrics.lowStockCount} en rupture</span>
                            ) : (
                              <span className="text-success">stock OK</span>
                            )}
                          </small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 col-6">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-3">
                          <div className="d-flex align-items-center mb-2">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2"
                              style={{ width: 36, height: 36, background: "rgba(45,106,79,0.1)" }}>
                              <i className="fas fa-percentage" style={{ fontSize: 14, color: "#2d6a4f" }} />
                            </div>
                            <small className="text-muted">Marge moyenne</small>
                          </div>
                          <h4 className="mb-0" style={{ fontWeight: 700 }}>
                            {avgMargin !== null ? `${avgMargin}%` : "-"}
                          </h4>
                          <small className="text-muted">
                            {avgMargin !== null
                              ? Number(avgMargin) > 20 ? "bonne marge" : Number(avgMargin) > 10 ? "marge correcte" : "marge faible"
                              : "pas de donnees"}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Charts Row 1: Revenue + Hours */}
                  <div className="row g-3 mb-6">
                    <div className="col-lg-8">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <h5 className="mb-0">Chiffre d'affaires</h5>
                            <div className="btn-group btn-group-sm" role="group">
                              {[
                                ["7j", "7j"],
                                ["30j", "30j"],
                                ["12s", "12 sem."],
                                ["12m", "12 mois"],
                              ].map(([val, label]) => (
                                <button
                                  key={val}
                                  type="button"
                                  className={`btn ${revenueRange === val ? "btn-dark" : "btn-outline-dark"}`}
                                  onClick={() => setRevenueRange(val)}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div style={{ height: 260 }}>
                            <Line data={revenueChartData} options={lineChartOpts} />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <h5 className="mb-3">Heures de pointe</h5>
                          <p className="text-muted mb-3" style={{ fontSize: 12 }}>
                            Volume des 30 derniers jours
                          </p>
                          <div style={{ height: 230 }}>
                            <Bar data={hoursChartData} options={barChartOpts} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Charts Row 2: Top Products + Payment Methods */}
                  <div className="row g-3 mb-6">
                    <div className="col-lg-7">
                      <div className="card border-0 shadow-sm">
                        <div className="card-body p-4">
                          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <h5 className="mb-0">Produits les plus vendus</h5>
                            <select
                              className="form-select form-select-sm"
                              style={{ width: "auto", minWidth: "100px", paddingRight: "2rem" }}
                              value={topCount}
                              onChange={(e) => setTopCount(Number(e.target.value))}
                            >
                              <option value={5}>Top 5</option>
                              <option value={10}>Top 10</option>
                              <option value={15}>Top 15</option>
                            </select>
                          </div>
                          {topProducts.length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-sm mb-0">
                                <thead>
                                  <tr>
                                    <th style={{ width: 30 }}>#</th>
                                    <th>Produit</th>
                                    <th className="text-end">Vendus</th>
                                    <th className="text-end">CA</th>
                                    <th className="text-end">Cmd</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {topProducts.map((p, i) => (
                                    <tr key={p.productId || i}>
                                      <td>
                                        <span className="badge bg-dark" style={{ fontSize: 10 }}>
                                          {i + 1}
                                        </span>
                                      </td>
                                      <td className="fw-semibold">{p.name}</td>
                                      <td className="text-end">{p.quantity}</td>
                                      <td className="text-end">{formatOrderAmount(p.revenue)}</td>
                                      <td className="text-end text-muted">{p.orders}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className="text-muted mb-0">Pas encore de ventes enregistrees.</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-5">
                      <div className="card border-0 shadow-sm">
                        <div className="card-body p-4">
                          <h5 className="mb-3">Modes de paiement</h5>
                          {paymentData.length > 0 ? (
                            <>
                              <div style={{ height: 200 }} className="mb-3">
                                <Doughnut data={paymentChartData} options={doughnutOpts} />
                              </div>
                              <ul className="list-unstyled mb-0">
                                {paymentData.map((p) => (
                                  <li key={p.method} className="d-flex justify-content-between py-1 border-bottom">
                                    <span>{p.method}</span>
                                    <span className="fw-semibold">
                                      {formatOrderAmount(p.revenue)}
                                      <small className="text-muted ms-1">({p.count})</small>
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </>
                          ) : (
                            <p className="text-muted mb-0">Aucun paiement enregistre.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Orders + Low Stock */}
                  <div className="row g-3 mb-6">
                    <div className="col-lg-8">
                      <div className="card border-0 shadow-sm">
                        <div className="card-body p-4">
                          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                            <h5 className="mb-0">Commandes recentes</h5>
                            <a href="/MyAcconutPaymentMethod" className="btn btn-outline-dark btn-sm">
                              Voir tout
                            </a>
                          </div>
                          <div className="table-responsive">
                            <table className="table table-sm mb-0 text-nowrap">
                              <thead className="table-light">
                                <tr>
                                  <th>Commande</th>
                                  <th>Client</th>
                                  <th>Date</th>
                                  <th>Articles</th>
                                  <th>Statut</th>
                                  <th>Paiement</th>
                                  <th className="text-end">Montant</th>
                                </tr>
                              </thead>
                              <tbody>
                                {recentOrders.map((order) => (
                                  <tr key={order.id}>
                                    <td className="fw-semibold">{order.orderNumber || order.id}</td>
                                    <td>{order.client}</td>
                                    <td>{formatOrderDate(order.date)}</td>
                                    <td>{order.itemCount || 0}</td>
                                    <td>
                                      <span className={`badge ${getOrderLifecycleBadgeClass(order)}`} style={{ fontSize: 10 }}>
                                        {getOrderLifecycleLabel(order)}
                                      </span>
                                    </td>
                                    <td>{order.paymentStatus}</td>
                                    <td className="text-end fw-semibold">{formatOrderAmount(order.amount)}</td>
                                  </tr>
                                ))}
                                {recentOrders.length === 0 && (
                                  <tr>
                                    <td colSpan={7} className="text-center text-muted py-4">
                                      Aucune commande.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-4">
                      <div className="card border-0 shadow-sm h-100">
                        <div className="card-body p-4">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Alertes stock</h5>
                            <a href="/MyAcconutNotification" className="btn btn-outline-dark btn-sm">
                              Stock
                            </a>
                          </div>
                          {lowStockProducts.length > 0 ? (
                            <ul className="list-unstyled mb-0">
                              {lowStockProducts.map((p) => (
                                <li key={p.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                  <div>
                                    <div className="fw-semibold" style={{ fontSize: 13 }}>{p.name}</div>
                                    <small className="text-muted">{p.category}</small>
                                  </div>
                                  <span className={`badge ${p.stock === 0 ? "bg-danger" : "bg-warning text-dark"}`}>
                                    {p.stock}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-muted mb-0">Tous les stocks sont OK.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Links */}
                  <div className="d-flex flex-wrap gap-2">
                    <a href="/MyAccountAddress" className="btn btn-outline-dark btn-sm">
                      <i className="fas fa-box-open me-1" /> Catalogue
                    </a>
                    <a href="/MyAcconutNotification" className="btn btn-outline-dark btn-sm">
                      <i className="fas fa-warehouse me-1" /> Stock
                    </a>
                    <a href="/MyAcconutPaymentMethod" className="btn btn-outline-dark btn-sm">
                      <i className="fas fa-receipt me-1" /> Commandes
                    </a>
                    <a href="/MyAccountCashier" className="btn btn-outline-dark btn-sm">
                      <i className="fas fa-cash-register me-1" /> Caisse
                    </a>
                    <a href="/MyAccountSuppliers" className="btn btn-outline-dark btn-sm">
                      <i className="fas fa-truck me-1" /> Fournisseurs
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MyAccountOrder;
